import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form as AntdForm } from "sunday-component/node_modules/antd";
import { MOBILE_NO_PATTERN } from "src/constants/region";
import {
  handleOnKeyPressNumberEvent,
  handleOnPasteNumberEvent,
} from "src/utils/appForm";
import { useRouter } from "next/router";
import { OTP_METHOD } from "src/constants/otp";
import { isEmpty } from "lodash";
import { transformToAntValidations } from "src/utils/validationRules";
import { CONSENT_CODE } from "src/constants/appForm";
import { createUser, updateUser } from "src/api/user";
import { logout } from "src/api/auth";
import { filterParams } from "src/utils/url";
import { GLOBAL_PARAMS } from "src/constants/params";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { logError } from "src/utils/logger";
import { useLoginOtp } from "../appForm/Otp/OtpLoginHelpersV2";
import {
  ButtonTitle,
  CancelModal,
  CancelModalBody,
  CloseIcon,
  ErrorModalBody,
  Form,
  FormItem,
  LoginModal,
  LoginModalBody,
  ModalTitle,
  SubmitButton,
  WelcomeBanner,
  WelcomeBannerImage,
  WelcomeModal,
  WelcomeModalBody,
  ErrorModal,
} from "./AuthModal.style";
import TextBoxForm, { TextBoxErrorText } from "../Common/Form/TextBoxForm";
import ConsentModal from "../appForm/ConsentModal";

export type AuthModalProps = {
  visible: boolean;
  onClose: (value: boolean) => void;
};

const AuthModal = ({ visible = false, onClose = () => {} }: AuthModalProps) => {
  const { t } = useTranslation(["layout", "error-dialog", "otp-form"]);
  const { locale, asPath, reload, push, query } = useRouter();
  const [form] = AntdForm.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConsentVisible, setIsConsentVisible] = useState<boolean>(false);
  const [isCancelConsentVisible, setIsCancelConsentVisible] =
    useState<boolean>(false);
  const [isWelcomeModalVisible, setIsWelcomeModalVisible] =
    useState<boolean>(false);
  const [isErrorModalVisible, setIsErrorModalVisible] =
    useState<boolean>(false);
  const { xl } = useBreakpoint();
  const redirectLink = asPath;
  const currentPage = `${process.env.NEXT_PUBLIC_BASE_PATH}${asPath}`;

  const mobileNoField = "mobileNo";
  const consentCode = CONSENT_CODE.SUNDAY_TC;

  const closeAllModals = () => {
    onClose(false);
    setIsConsentVisible(false);
    setIsCancelConsentVisible(false);
    setIsErrorModalVisible(false);
  };

  const onFinishVerified = (isRegister: boolean) => {
    if (isRegister) {
      setIsConsentVisible(true);
    } else {
      reload();
    }
  };

  const {
    OtpFormSuffix: OtpFormSuffixMobile,
    setIsRequestOtpDisable,
    setOtpRef: setOtpRefMobile,
    setIsOtpVerified: setOtpVerifiedMobile,
  } = useLoginOtp({
    active: true,
    method: OTP_METHOD.SMS,
    form,
    code: "basic-insured-person",
    field: mobileNoField,
    lang: locale,
    otpField: "mobileOtp",
    i18nField: `modal.mobileNo`,
    errorText: undefined,
    urlLink: redirectLink,
    buttonLabels: {
      submitButtonLabel: t("modal.submitButton"),
      verifyButtonLabel: t("modal.submitButton"),
    },
    modalTitle: t("otp-form:titleRegister"),
    desktopBreakpoint: xl,
    onFinishVerifying: onFinishVerified,
  });

  const onCancelConsent = () => {
    setIsCancelConsentVisible(true);
  };

  const onAgreeConsent = async () => {
    setIsLoading(true);
    try {
      const updateConsentsPayload = {
        consents: {
          [consentCode]: true,
        },
      };
      await updateUser(updateConsentsPayload);
      await createUser();
      closeAllModals();
      setIsWelcomeModalVisible(true);
    } catch (error) {
      const { response } = error;
      let issueTitle = `Unknown Error Occurred with status code ${response.status}`;
      if (error.code && error.type) {
        issueTitle = `[${error?.code}] ${error?.type}`;
      }
      const message = error.message || response?.data?.message;
      setIsErrorModalVisible(true);
      logError(message, response.data, response, issueTitle);
    }
    setIsLoading(false);
  };

  const validateMobileNumber = (_: any, value: string, submitError) => {
    if (!MOBILE_NO_PATTERN.test(value)) {
      return submitError(
        <TextBoxErrorText>{t(`modal.mobileNo.error.pattern`)}</TextBoxErrorText>
      );
    }
    setOtpRefMobile("");
    return submitError();
  };

  const handleOnChangeMobile = () => {
    setOtpVerifiedMobile(false);
  };

  const onFieldsChange = async () => {
    if (isEmpty(form.getFieldError(mobileNoField))) {
      setIsRequestOtpDisable(false);
    } else {
      setIsRequestOtpDisable(true);
    }
  };

  const onCancel = async () => {
    await logout();
    reload();
  };

  const onGetStart = () => {
    push({
      pathname: `/user/sign-up/personal-profile`,
      query: { ...filterParams(query, GLOBAL_PARAMS), r: currentPage },
    });
  };

  const onCloseErrorModal = () => {
    closeAllModals();
  };

  return (
    <>
      <LoginModal
        centered={xl}
        visible={visible}
        onCancel={() => onClose(false)}
        footer={
          <Form
            data-testid="login-otp-form"
            form={form}
            onFieldsChange={onFieldsChange}
          >
            <FormItem>{OtpFormSuffixMobile}</FormItem>
          </Form>
        }
        closeIcon={
          <CloseIcon
            preview={false}
            src="/static/pages/planPage/x-icon.svg"
            alt="close-icon"
          />
        }
        title={<ModalTitle>{t("modal.title")}</ModalTitle>}
      >
        <LoginModalBody>
          {t("modal.subtitle")}
          <Form
            data-testid="login-form"
            form={form}
            onFieldsChange={onFieldsChange}
          >
            <FormItem
              className={mobileNoField}
              name={mobileNoField}
              validateFirst
              rules={[
                ...transformToAntValidations(
                  [{ rule: "required" }],
                  t,
                  t(`modal.mobileNo.label`).toLowerCase(),
                  false,
                  TextBoxErrorText
                ),
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <TextBoxForm
                key={mobileNoField}
                type="number"
                onChange={handleOnChangeMobile}
                onKeyPress={handleOnKeyPressNumberEvent}
                onPaste={handleOnPasteNumberEvent}
                placeholder={t(`modal.mobileNo.placeholder`)}
                label={t(`modal.mobileNo.label`)}
              />
            </FormItem>
          </Form>
        </LoginModalBody>
      </LoginModal>
      <ConsentModal
        code={consentCode}
        visible={isConsentVisible}
        lang={locale}
        title={t("modal.consent.title")}
        onCloseModal={onCancelConsent}
        footer={
          <SubmitButton onClick={onAgreeConsent} loading={isLoading}>
            <ButtonTitle>{t("modal.consent.submitButton")}</ButtonTitle>
          </SubmitButton>
        }
      />
      <CancelModal
        visible={isCancelConsentVisible}
        onCancel={onCancel}
        centered
        closable={false}
        maskClosable={false}
        footer={[
          <SubmitButton onClick={() => setIsCancelConsentVisible(false)}>
            <ButtonTitle>{t("modal.cancelConsent.submitButton")}</ButtonTitle>
          </SubmitButton>,
          <SubmitButton onClick={onCancel} type="outlined">
            <ButtonTitle>{t("modal.cancelConsent.cancelButton")}</ButtonTitle>
          </SubmitButton>,
        ]}
        title={<ModalTitle>{t("modal.cancelConsent.title")}</ModalTitle>}
      >
        <CancelModalBody>{t("modal.cancelConsent.subtitle")}</CancelModalBody>
      </CancelModal>
      <WelcomeModal
        visible={isWelcomeModalVisible}
        centered
        closable={false}
        maskClosable={false}
        footer={[
          <SubmitButton onClick={onGetStart}>
            <ButtonTitle>{t("modal.welcome.button")}</ButtonTitle>
          </SubmitButton>,
        ]}
      >
        <WelcomeBanner>
          <WelcomeBannerImage
            preview={false}
            src="/static/pages/registerOtp/register-welcome.svg"
            alt="welcome-image"
          />
        </WelcomeBanner>
        <WelcomeModalBody>
          <ModalTitle>{t("modal.welcome.title")}</ModalTitle>
          {t("modal.welcome.subtitle")}
        </WelcomeModalBody>
      </WelcomeModal>
      <ErrorModal
        title={t("error-dialog:title")}
        description={
          <ErrorModalBody>{t("error-dialog:description")}</ErrorModalBody>
        }
        errorButtons={[
          {
            text: t("error-dialog:closeButton"),
            onClick: () => onCloseErrorModal(),
          },
        ]}
        isVisible={isErrorModalVisible}
        width="303px"
      />
    </>
  );
};

export default AuthModal;
